import { SearchResultObject, SearchState, SearchStatus } from './types';
import {
  Box,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Tab,
  SxProps,
} from '@mui/material';
import React, {
  ReactNode,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import { Typography } from '@platform-ui/design-system';
import { SearchIcon } from './Icons';
import { TabContext, TabList, TabPanel, TabPanelProps } from '@mui/lab';
import { TabListProps } from '@mui/lab/TabList/TabList';
import { SearchContext } from './index';
import { deepActiveElement } from './utils';
import { useTranslation } from 'react-i18next';
import {
  FOCUSABLE_ELEMENT_CLASS,
  OBJECT_TYPE_PAGES,
  SEARCH_TERM_PARAM_KEY,
  TAB_LIST_WIDTH,
} from './Constants';
import ReactHtmlParser from 'react-html-parser';
import { useCurrentIdentity } from '../nav/hooks/useIdentity';
import { useFormatters } from '../nav/hooks/useFormatter';
import { AmplitudeSearchEvents, useAmplitude } from '../nav/hooks/useAmplitude';
import { convertToBillingUrl } from '../nav/utils/getBillingBaseUrl';
import { useSearchMetadata } from './useSearchMetadata';
import { StatusChip } from './StatusChip';
import { CurrencyField, SearchItemViewModel, SearchMeta } from './meta/types';
import { searchMetadata } from './meta';
import { appendSearchHistory, getSearchHistory } from './searchHistory';
import { escapeRegExp } from 'lodash';
import DOMPurify from 'dompurify';

const PRIMARY_COLOR = 'rgba(0, 0, 0, 0.9)';
const SECONDARY_COLOR = 'rgba(0, 0, 0, 0.6)';
const BACKGROUND_COLOR = 'white';
const PROMPT_COLOR = 'rgba(0, 0, 0, 0.4)';
const RESULT_WRAPPER_CLASS = 'search-result-wrapper';
const TAB_CLASS = 'search-result-tab';
const TAB_E2E = 'search-tab';
const TAB_HEIGHT = 40;

const PlaceholderWrapper = styled.div`
  box-sizing: border-box;
  color: ${PRIMARY_COLOR};
  padding: 8px 16px;
  width: 100%;
  flex-grow: 0;
`;

const TruncatedBox: React.FC<{
  children: React.ReactNode;
  title: string;
  sx?: SxProps;
}> = ({ children, title, sx }) => {
  return (
    <Box
      sx={{
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        ...sx,
      }}
      title={title}
    >
      {children}
    </Box>
  );
};

const Loading = (): JSX.Element => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
      <CircularProgress />
    </Box>
  );
};

const NoResult = (keyword: string, t): JSX.Element => {
  return (
    <PlaceholderWrapper>
      <TruncatedBox title={keyword}>
        <Typography variant="body2">
          {t('GlobalSearch.NoResult')}
          &quot;<b>{keyword}</b>&quot;
        </Typography>
      </TruncatedBox>
      <Box sx={{ marginTop: '1rem' }}>
        <Typography variant="body2">{t('GlobalSearch.Retry')}</Typography>
      </Box>
    </PlaceholderWrapper>
  );
};

const IdlePrompt = (meta: SearchMeta, t): JSX.Element => {
  return (
    <PlaceholderWrapper>
      <Typography variant="body2">
        {t('GlobalSearch.EnterKeyword', [t(meta.objectLabelI18n)])}
        {!!meta.listPageUrl && (
          <Link href={meta.listPageUrl} className={FOCUSABLE_ELEMENT_CLASS}>
            {t('GlobalSearch.ClickToViewAll', [t(meta.objectLabelI18n)])}
          </Link>
        )}
      </Typography>
    </PlaceholderWrapper>
  );
};

const Error: React.FC<{ message?: string; requestId?: string }> = ({
  message,
  requestId,
}) => {
  return (
    <PlaceholderWrapper data-e2e="search-error-wrapper">
      {message && <Typography variant="body2">{message}</Typography>}
      {requestId && (
        <Box sx={{ marginTop: '1rem' }}>
          <Typography variant="body2">Request ID: {requestId}</Typography>
        </Box>
      )}
    </PlaceholderWrapper>
  );
};

const ResultRow: React.FC<any> = ({
  multiline,
  children,
  href,
  onClickOrEnter,
  ...props
}) => {
  return (
    <Link
      {...props}
      className={FOCUSABLE_ELEMENT_CLASS}
      tabIndex={0}
      data-e2e="search-result-row"
      sx={{
        textDecoration: 'inherit',
        color: 'inherit',
        '&:focus': {
          backgroundColor: 'rgba(13, 74, 195, 0.08)',
        },
      }}
      href={href}
      onClick={onClickOrEnter}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onClickOrEnter();
        }
      }}
    >
      <Box
        sx={{
          boxSizing: 'border-box',
          padding: '8px 16px',
          ...(multiline
            ? {
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }
            : {}),
          cursor: 'pointer',
          height: multiline ? '60px' : '36px',
          '&:hover, &:focus': {
            backgroundColor: 'rgba(13, 74, 195, 0.08)',
          },
        }}
      >
        {children}
      </Box>
    </Link>
  );
};

const DotDivider = (): JSX.Element => {
  return (
    <Typography component="span" sx={{ color: PROMPT_COLOR }} variant="body2">
      •
    </Typography>
  );
};

function focusFirstResult() {
  const root = document.activeElement?.shadowRoot || document;
  const firstResult = root.querySelector(
    `.${RESULT_WRAPPER_CLASS}`,
  )?.firstElementChild;
  (firstResult as HTMLElement)?.focus();
}

function focusCurrentTab() {
  const root = document.activeElement?.shadowRoot || document;
  const currentTabElement = root.querySelector(`.${TAB_CLASS}[tabindex="0"]`);
  (currentTabElement as HTMLElement)?.focus();
}

const ResultWrapper: React.FC = ({ children }) => {
  return (
    <Stack
      direction="column"
      flexWrap="nowrap"
      sx={{
        height: '100%',
        width: '100%',
        overflowY: 'scroll',
        boxSizing: 'border-box',
        borderRadius: '0 0 0 4px',
      }}
      className={RESULT_WRAPPER_CLASS}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const activeElement = deepActiveElement();
          if (activeElement && activeElement.nextElementSibling) {
            activeElement.nextElementSibling.focus();
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const activeElement = deepActiveElement();
          if (activeElement && activeElement.previousElementSibling) {
            activeElement.previousElementSibling.focus();
          }
        } else if (e.key === 'ArrowLeft') {
          focusCurrentTab();
        }
      }}
    >
      {children}
    </Stack>
  );
};

const DescriptionRowWrapper: React.FC = ({ children }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        minWidth: '0',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      {children}
    </Box>
  );
};

const TitleRowWrapper: React.FC = ({ children }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        minWidth: '0',
      }}
    >
      {children}
    </Box>
  );
};

const HighlightedText = ({ text, keyword }) => {
  const escapedKeyword = escapeRegExp(keyword);
  const sanitizedText = DOMPurify.sanitize(text);
  const regex = new RegExp(`(${escapedKeyword})`, 'ig');
  const parts = sanitizedText.split(regex);
  return (
    <>
      {parts.map((part, index) => {
        if (part.toLowerCase() === keyword.toLowerCase()) {
          return <strong key={index}>{part}</strong>;
        }
        return part;
      })}
    </>
  );
};

const SearchResultList = (
  keyword: string,
  meta: SearchMeta,
  result: SearchResultObject,
  searchCountResult: SearchResultObject,
  addHistory: (keyword: string) => void,
  t,
  formatter,
) => {
  const titleField = (title: string) => {
    return (
      <TruncatedBox sx={{ flex: '0 1 auto' }} title={title}>
        <Typography component="span" variant="body2">
          <HighlightedText text={title} keyword={keyword} />
        </Typography>
      </TruncatedBox>
    );
  };

  const descriptionFields = (item: SearchItemViewModel) => {
    const nonEmptyFields = item.descriptionFields.filter(
      (field) => field.value !== undefined,
    );
    if (!nonEmptyFields.length) return null;
    return (
      <>
        {nonEmptyFields
          .map((field, index) => {
            let content: ReactNode = '';
            const title = field.title || '';
            if (field.type === 'Text') {
              content = (
                <HighlightedText text={field.value} keyword={keyword} />
              );
            } else if (field.type === 'Currency') {
              field = field as CurrencyField;
              content = formatter.currency(field.value, field.currency);
            } else if (field.type === 'Date') {
              content = formatter.date(field.value);
            } else {
              return null;
            }

            return (
              <>
                {index > 0 && <DotDivider />}
                <TruncatedBox
                  key={index}
                  sx={{
                    color: SECONDARY_COLOR,
                    flex: '0 0 auto',
                    flexShrink:
                      field.type === 'Text' && field.value.length > 20 ? 1 : 0,
                  }}
                  title={title}
                >
                  <Typography component="span" variant="caption">
                    {content}
                  </Typography>
                </TruncatedBox>
              </>
            );
          })
          .filter((el) => el !== null)}
      </>
    );
  };

  const chipField = (item: SearchItemViewModel) => {
    if (!item.chipField) return null;
    const { state, label, title } = item.chipField;
    return <StatusChip state={state} label={label} title={title} />;
  };

  return (
    <ResultWrapper>
      {result.objects.map((resultItem, index) => {
        const item = meta.renderItem?.(resultItem, t);
        if (!item) {
          return null;
        }
        const renderedDescriptionFields = descriptionFields(item);
        const content = (
          <>
            <TitleRowWrapper>
              {titleField(item.title)}
              {chipField(item)}
            </TitleRowWrapper>
            {!!renderedDescriptionFields && (
              <DescriptionRowWrapper>
                {renderedDescriptionFields}
              </DescriptionRowWrapper>
            )}
          </>
        );
        return (
          <ResultRow
            multiline={!!renderedDescriptionFields}
            href={item.href}
            key={`${meta.objectType}_${index}`}
            onClickOrEnter={() => addHistory(keyword)}
          >
            {content}
          </ResultRow>
        );
      })}
      {meta.hasAdvancedSearch && (
        <Link
          onClick={() => addHistory(keyword)}
          sx={{
            textDecoration: 'inherit',
            color: 'inherit',
            marginTop: 'auto',
            borderRadius: '0 0 4px 0',
          }}
          href={convertToBillingUrl(
            `/platform/apps/search?searchTerm=${keyword}&searchObjectType=${meta.objectType.toLowerCase()}`,
          )}
          key="view_all"
          className={FOCUSABLE_ELEMENT_CLASS}
        >
          <Box
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              height: '32px',
              backgroundColor: '#F8F9FB',
              padding: '8px 16px',
              borderRadius: '0 0 4px 0',
            }}
          >
            <TruncatedBox title={keyword}>
              <Typography variant="body2">
                {ReactHtmlParser(
                  t('GlobalSearch.ViewAll', [
                    t(meta.objectLabelI18n),
                    keyword,
                    searchCountResult?.totalCount > 999
                      ? '999+'
                      : searchCountResult?.totalCount,
                  ]),
                )}
              </Typography>
            </TruncatedBox>
          </Box>
        </Link>
      )}
    </ResultWrapper>
  );
};

const StyledTabPanel = styled(TabPanel)<TabPanelProps>`
  min-width: 0;
  flex-grow: 1;
  padding: 0;
  background-color: ${BACKGROUND_COLOR};
  border-radius: 0 0 4px 0;
`;

const StyledTabList = styled((props: TabListProps) => (
  <TabList
    {...props}
    TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
  />
))({
  borderRadius: '0 0 0 4px',
  flexBasis: `${TAB_LIST_WIDTH}px`,
  flexShrink: '0',
  backgroundColor: BACKGROUND_COLOR,
  borderRight: 'solid 1px rgba(0, 0, 0, 0.08)',
  '.MuiTabs-flexContainer': {
    alignItems: 'flex-start',
  },
  '& .MuiTabs-indicator': {
    width: '3px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: 'inherit',
  },
  '& .MuiTabs-indicatorSpan': {
    height: '50%',
    backgroundColor: '#00D2B9',
  },
});

type StyledTabProps = {
  label: ReactNode;
  disabled?: boolean;
  value: string;
  className?: string;
  grayout?: boolean;
};

const StyledTab = styled((props: StyledTabProps) => <Tab {...props} />)(
  (props) => ({
    '&.MuiTab-root': {
      minHeight: `${TAB_HEIGHT}px`,
      padding: '8px 16px',
      color: props.grayout ? 'rgba(0,0,0,0.3)' : PRIMARY_COLOR,
      fontWeight: 'normal',
      width: '100%',
      display: 'flex',
      alignItems: 'flex-start',
    },
    '&.Mui-selected': {
      fontWeight: 'bold',
    },
  }),
);

const TabLabel: React.FC<{
  icon: ReactNode;
  title: string;
}> = ({ title, icon }) => {
  return (
    <Stack
      spacing={1}
      flexWrap="nowrap"
      direction="row"
      alignItems="center"
      sx={{ margin: '0 4px', textTransform: 'none' }}
    >
      {icon}
      <span>{title}</span>
    </Stack>
  );
};

const SearchHistory: React.FC<{
  history: string[];
  onClick: (keyword: string) => void;
}> = ({ history, onClick }) => {
  return (
    <ResultWrapper>
      {history.map((keyword) => (
        <ResultRow
          key={`history_${keyword}`}
          onClickOrEnter={() => onClick(keyword)}
        >
          <Stack direction="row" spacing={1} sx={{ minWidth: '0' }}>
            <i style={{ flexShrink: 0 }}>
              <SearchIcon />
            </i>
            <TruncatedBox title={keyword}>
              <Typography variant="body2">{keyword}</Typography>
            </TruncatedBox>
          </Stack>
        </ResultRow>
      ))}
    </ResultWrapper>
  );
};

const LoadingTab = (): JSX.Element => {
  return (
    <StyledTab
      label={
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}>
          <CircularProgress size="1rem" />
        </Box>
      }
      value="loading"
      disabled
    />
  );
};

type SearchResultProps = {
  state: SearchState[];
};

export type SearchResultComponent = {
  focusFirstResult: () => void;
  goToAdvancedPage: () => void;
};

export const SearchResult = React.forwardRef<
  SearchResultComponent,
  SearchResultProps
>(({ state }: SearchResultProps, ref) => {
  const [quickSearchState, searchCountState] = state;
  const { data: searchCountData } = searchCountState;
  const {
    status,
    data: resultData,
    error: quickSearchError,
  } = quickSearchState;
  const {
    data: objectMeta,
    isLoading: isMetaLoading,
    error: metaError,
  } = useSearchMetadata();

  let metadata = [...searchMetadata];
  if (!isMetaLoading && !metaError && objectMeta?.length) {
    metadata = metadata.filter(
      (vm) =>
        !vm.needsPermission ||
        objectMeta.some((meta) => meta.type === vm.objectType),
    );
  } else {
    metadata = metadata.filter((obj) => !obj.needsPermission);
  }

  const [currentTab, setCurrentTab] = useState<string>('0');
  const { keyword, setKeyword } = useContext(SearchContext);
  const { t } = useTranslation();
  const { locale } = useCurrentIdentity();
  const formatter = useFormatters({ locale });
  const { logEvent } = useAmplitude();
  const containerRef = useRef<any>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    if (height !== containerRef.current?.clientHeight) {
      setHeight(containerRef.current?.clientHeight);
    }
  }, [metadata.length]);

  const logViewEvent = (objectType, totalCount) => {
    logEvent(AmplitudeSearchEvents.VIEW_QUICK_SEARCH_RESULT, {
      objectType,
      totalCount,
    });
  };

  const selectedObjectType = (tabValue): string => {
    return metadata[parseInt(tabValue)].objectType;
  };

  const resultMap = resultData.reduce(
    (acc, next) => ({ ...acc, [next.objectType]: next }),
    {},
  );

  const searchCountMap = searchCountData?.reduce(
    (acc, next) => ({ ...acc, [next.objectType]: next }),
    {},
  );

  const handleTabChange = (newValue: string) => {
    if (currentTab === newValue) return;
    setCurrentTab(newValue);
    const objectType = selectedObjectType(newValue);
    logViewEvent(objectType, searchCountMap[objectType]?.totalCount ?? 0);
  };

  const tabContent = (meta: SearchMeta) => {
    if (status === SearchStatus.Idle) {
      const history = getSearchHistory();
      if (history.length > 0) {
        return (
          <SearchHistory
            history={history}
            onClick={(searchTerm) => {
              logEvent(AmplitudeSearchEvents.CLICK_SEARCH_HISTORY, {
                searchTerm: searchTerm,
              });
              setKeyword(searchTerm);
              appendSearchHistory(searchTerm);
            }}
          />
        );
      } else {
        return IdlePrompt(meta, t);
      }
    } else if (status === SearchStatus.Loading) {
      return <Loading />;
    } else if (status === SearchStatus.Error) {
      return (
        <Error
          message={quickSearchError?.message}
          requestId={quickSearchError?.requestId}
        />
      );
    }

    const result = resultMap[meta.objectType];
    const searchCountResult = searchCountMap[meta.objectType];
    if (!result?.objects.length) {
      return NoResult(keyword, t);
    }
    return SearchResultList(
      keyword,
      meta,
      result,
      searchCountResult,
      appendSearchHistory,
      t,
      formatter,
    );
  };

  useEffect(() => {
    // if the current tab has no results, jump to the first tab that has results
    if (status !== SearchStatus.Success || !resultData.length) return;
    const objectType = selectedObjectType(currentTab);
    const currentTabResult = resultMap[objectType];
    if (currentTabResult && !!currentTabResult.objects.length) {
      logViewEvent(objectType, searchCountMap[objectType]?.totalCount ?? 0);
      return;
    }
    for (const i in metadata) {
      const result = resultMap[metadata[i].objectType];
      if (result && result.objects.length > 0) {
        handleTabChange(i.toString());
        return;
      }
    }
    logViewEvent(objectType, 0);
  }, [status, JSON.stringify(resultData)]);

  useImperativeHandle(
    ref,
    () => ({
      focusFirstResult: focusFirstResult,
      goToAdvancedPage() {
        appendSearchHistory(keyword);
        let href = `/platform/apps/search?${SEARCH_TERM_PARAM_KEY}=${keyword}`;
        const currentObject = selectedObjectType(currentTab);
        if (currentObject !== OBJECT_TYPE_PAGES) {
          href += `&searchObjectType=${currentObject.toLowerCase()}`;
        }
        window.location.href = href;
      },
    }),
    [currentTab, keyword],
  );

  return (
    <Paper
      ref={containerRef}
      elevation={12}
      sx={{
        width: '100%',
        display: 'flex',
        color: PRIMARY_COLOR,
        borderRadius: '0 0 4px 4px',
      }}
    >
      <TabContext value={currentTab}>
        <StyledTabList
          orientation="vertical"
          onChange={(e, value) => handleTabChange(value)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') {
              focusFirstResult();
              return;
            }

            /**
             * Navigating with arrow keys should be natively supported with the 'selectionFollowsFocus' property.
             * But it doesn't work in web components because it's using 'document.activeElement'
             * to find the element in focus,
             * which points to the hosting element and causes early return in its source code.
             * Source: https://github.com/mui/material-ui/blob/2d58fb8b6ac5731e84103851fa0a81b25a49d50d/packages/mui-material/src/Tabs/Tabs.js#L668
             */
            let next = 0;
            const numberOfTabs = metadata.length;
            if (e.key === 'ArrowDown') {
              next = (parseInt(currentTab) + 1) % numberOfTabs;
            } else if (e.key === 'ArrowUp') {
              next = (parseInt(currentTab) - 1 + numberOfTabs) % numberOfTabs;
            } else {
              return;
            }
            const nextElement = deepActiveElement()?.parentNode?.children[next];
            if (nextElement) {
              nextElement?.focus();
              handleTabChange(next.toString());
            }
            e.preventDefault();
          }}
        >
          {metadata.map(({ tabIcon, objectLabelI18n, objectType }, index) => (
            <StyledTab
              data-e2e={`${TAB_E2E}-${objectType}`}
              className={`${TAB_CLASS} ${FOCUSABLE_ELEMENT_CLASS}`}
              label={<TabLabel icon={tabIcon} title={t(objectLabelI18n)} />}
              value={index.toString()}
              key={objectType}
              grayout={
                (status === SearchStatus.Success &&
                  !resultMap[objectType]?.objects?.length) as any
              }
            />
          ))}
          {isMetaLoading && <LoadingTab />}
        </StyledTabList>
        <StyledTabPanel value={currentTab} sx={{ height: `${height}px` }}>
          <Box
            sx={{
              height: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            {tabContent(metadata[parseInt(currentTab)])}
          </Box>
        </StyledTabPanel>
      </TabContext>
    </Paper>
  );
});
